import { Navigate, Route, createRoutesFromElements } from "react-router-dom";
import { PageNavigationLayout } from "@/layouts/PageNavigationLayout";
import { AppLayout } from "@/layouts/AppLayout";
import { AuthLayout } from "@/layouts/AuthLayout";
import { ProtectedRoute } from "./ProtectedRoute";
import { DashboardPage } from "@/modules/dashboard/DashboardPage";
import { ContactsPage } from "@/modules/contacts/ContactsPage";
import { PropertiesPage } from "@/modules/properties/PropertiesPage";
import { UsersPage } from "@/modules/users/UsersPage";
import { BranchesPage } from "@/modules/branches/BranchesPage";
import { LegalEntitiesPage } from "@/modules/legal-entities/LegalEntitiesPage";
import { LeadsPage } from "@/modules/leads/LeadsPage";
import { CampaignsPage } from "@/modules/campaigns/CampaignsPage";
import { LabelsPage } from "@/modules/labels/LabelsPage";
import { DiaryPage } from "@/modules/diary/DiaryPage";
import { DocumentsPage } from "@/modules/documents/DocumentsPage";
import { ImagesPage } from "@/modules/images/ImagesPage";
import { ReachOutPage } from "@/modules/conversation/ReachOutPage";
import { IntegrationsPage } from "@/modules/integrations/IntegrationsPage";
import { SignupPage } from "@/modules/auth/SignupPage";
import { ProfileSetupPage } from "@/modules/profile/ProfileSetupPage";
import { AccessControlPage } from "@/modules/access-control/AccessControlPage";
import { SettingsPage } from "@/modules/settings/SettingsPage";
import { VerifyCodePage } from "@/modules/integrations/VerifyCodePage";
import { CampaignsLabelsRoute } from "./CampaignsLabelsRoute";

/**
 * Route tree for the data router. Declared with `createRoutesFromElements` so
 * the JSX shape stays as-is, while giving the app a data router — which is what
 * `useBlocker` (unsaved-changes guards) requires.
 */
export const appRoutes = createRoutesFromElements(
  <Route element={<PageNavigationLayout />}>
      {/* Public Auth Routes */}
      <Route path="/auth" element={<AuthLayout />}>
        <Route path="signup" element={<SignupPage />} />
        <Route path="login" element={<Navigate to="/" replace />} />
      </Route>

      {/* Protected App Routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="contacts" element={<ContactsPage />} />
          <Route path="properties" element={<PropertiesPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="branches" element={<BranchesPage />} />
          <Route path="legal-entities" element={<LegalEntitiesPage />} />
          <Route path="leads" element={<LeadsPage />} />
          <Route
            path="campaigns"
            element={
              <CampaignsLabelsRoute>
                <CampaignsPage />
              </CampaignsLabelsRoute>
            }
          />
          <Route
            path="labels"
            element={
              <CampaignsLabelsRoute>
                <LabelsPage />
              </CampaignsLabelsRoute>
            }
          />
          {/* Singular spelling kept working, since that is what the page was asked for as. */}
          <Route
            path="label"
            element={
              <CampaignsLabelsRoute>
                <Navigate to="/labels" replace />
              </CampaignsLabelsRoute>
            }
          />
          <Route path="diary" element={<DiaryPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="images" element={<ImagesPage />} />
          <Route path="conversations" element={<div />} />
          <Route path="reach-out" element={<ReachOutPage />} />

          <Route path="integrations" element={<IntegrationsPage />} />
          <Route path="integrations/:crm" element={<IntegrationsPage />} />
          <Route path="messages/verifycode" element={<VerifyCodePage />} />
          <Route path="profile" element={<ProfileSetupPage />} />
          <Route path="access-control" element={<AccessControlPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
  </Route>,
);
